import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { MarketingLayout } from './layout/Layout';
import { HeroSection } from './HeroSection';
import { 
  Check, 
  X, 
  Star, 
  Crown, 
  Zap, 
  Shield,
  ArrowRight,
  Users,
  Calendar,
  BarChart3,
  CreditCard,
  Wallet,
  Settings,
  Lock,
  TrendingUp,
  DollarSign,
  Globe,
  Loader2,
  Heart
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';

export const HomePage = () => {
  const [billingCycle, setBillingCycle] = useState('monthly'); // 'monthly' or 'annual'
  const [pricingData, setPricingData] = useState(null);
  const [pricingLoading, setPricingLoading] = useState(true);
  const [pricingError, setPricingError] = useState(null);
  const [couponCode, setCouponCode] = useState('');
  const [couponValidating, setCouponValidating] = useState(false);
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponError, setCouponError] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [userLoading, setUserLoading] = useState(true);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

  // Fetch pricing data and user information from API
  useEffect(() => {
    fetchPricingData();
    fetchCurrentUser();
  }, []);

  const fetchCurrentUser = async () => {
    try {
      setUserLoading(true);
      const response = await fetch(`${BACKEND_URL}/api/auth/me`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const userData = await response.json();
        setCurrentUser(userData.user);
        
        // Fetch user's subscription if authenticated
        const subscriptionResponse = await fetch(`${BACKEND_URL}/api/user/subscription`, {
          credentials: 'include'
        });
        
        if (subscriptionResponse.ok) {
          const subscriptionData = await subscriptionResponse.json();
          setCurrentPlan(subscriptionData.plan);
        }
      }
    } catch (error) {
      console.log('User not authenticated or error fetching user data:', error);
      // This is fine for public homepage
    } finally {
      setUserLoading(false);
    }
  };

  const fetchPricingData = async () => {
    try {
      setPricingLoading(true);
      setPricingError(null);
      
      const response = await fetch(`${BACKEND_URL}/api/public/plans`);
      const data = await response.json();
      
      if (response.ok && data.plans && data.plans.length > 0) {
        setPricingData(data);
      } else {
        // Fallback to static data if API fails
        setPricingData({ source: 'fallback', plans: [] });
      }
    } catch (error) {
      console.error('Failed to fetch pricing data:', error);
      setPricingError('Failed to load pricing data');
      // Use fallback static data
      setPricingData({ source: 'fallback', plans: [] });
    } finally {
      setPricingLoading(false);
    }
  };

  // Validate coupon code
  const validateCoupon = async (code, planKey) => {
    if (!code.trim()) {
      setCouponError('Please enter a coupon code');
      return;
    }

    try {
      setCouponValidating(true);
      setCouponError('');
      
      const response = await fetch(`${BACKEND_URL}/api/v1/coupons/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ coupon_code: code, plan_key: planKey })
      });
      
      const data = await response.json();
      
      if (data.valid) {
        setAppliedCoupon(data.coupon);
        setCouponError('');
      } else {
        setAppliedCoupon(null);
        setCouponError(data.error || 'Invalid coupon code');
      }
    } catch (error) {
      console.error('Coupon validation error:', error);
      setCouponError('Failed to validate coupon');
    } finally {
      setCouponValidating(false);
    }
  };

  // Calculate discounted price
  const calculateDiscountedPrice = (originalPrice, coupon) => {
    if (!coupon || !originalPrice) return originalPrice;
    
    if (coupon.discount_type === 'percentage') {
      return originalPrice * (1 - coupon.discount_value / 100);
    } else if (coupon.discount_type === 'fixed_amount') {
      return Math.max(0, originalPrice - coupon.discount_value);
    }
    
    return originalPrice;
  };

  // Get plan icon based on plan key
  const getPlanIcon = (planKey) => {
    switch (planKey) {
      case 'starter': return <Heart className="w-6 h-6" />;
      case 'personal': return <Star className="w-6 h-6" />;
      case 'professional': return <Crown className="w-6 h-6" />;
      case 'enterprise': return <Zap className="w-6 h-6" />;
      default: return <Star className="w-6 h-6" />;
    }
  };

  // Determine if plan is current, upgrade, or downgrade
  const getPlanRelationship = (planKey) => {
    if (!currentPlan || !currentPlan.key) return 'available';
    if (currentPlan.key === planKey) return 'current';
    
    // Plan tier hierarchy: starter (0) -> personal (1) -> professional (2) -> enterprise (3)
    const planTiers = { 'starter': 0, 'personal': 1, 'professional': 2, 'enterprise': 3 };
    const currentTier = planTiers[currentPlan.key] || 0;
    const targetTier = planTiers[planKey] || 0;
    
    if (targetTier > currentTier) return 'upgrade';
    if (targetTier < currentTier) return 'downgrade';
    return 'available';
  };

  // Get plan relationship label
  const getPlanLabel = (planKey) => {
    const relationship = getPlanRelationship(planKey);
    switch (relationship) {
      case 'current': return 'Your current plan';
      case 'upgrade': return 'Upgrade';
      case 'downgrade': return 'Downgrade';
      default: return null;
    }
  };

  // Check if plan CTA should be disabled
  const isPlanCTADisabled = (planKey) => {
    return getPlanRelationship(planKey) === 'current';
  };

  // Fallback static pricing data (in case API fails)
  const fallbackPricingPlans = [
    {
      name: 'Starter',
      description: 'Perfect for individuals getting started',
      icon: <Heart className="w-6 h-6" />,
      price: {
        monthly: 0,
        annual: 0
      },
      badge: null,
      key: 'starter',
      features: [
        { name: 'Up to 25 contacts', included: true },
        { name: 'Basic relationship mapping', included: true },
        { name: 'Simple check-in reminders', included: true },
        { name: 'Contact import (CSV only)', included: true },
        { name: 'Mobile app access', included: true },
        { name: 'Voice interaction', included: false },
        { name: 'AI-powered insights', included: false },
        { name: 'Gift planning', included: false },
        { name: 'Social radar', included: false },
        { name: 'Advanced analytics', included: false },
        { name: 'Team collaboration', included: false },
        { name: 'Priority support', included: false }
      ],
      cta: 'Start Free Forever',
      popular: false
    },
    {
      name: 'Personal',
      description: 'For individuals who want the full experience',
      icon: <Star className="w-6 h-6" />,
      price: {
        monthly: 12,
        annual: 120 // $10/month when paid annually
      },
      badge: 'Most Popular',
      key: 'personal',
      features: [
        { name: 'Up to 200 contacts', included: true },
        { name: 'Advanced relationship mapping', included: true },
        { name: 'Smart check-in reminders', included: true },
        { name: 'All contact import methods', included: true },
        { name: 'Mobile app access', included: true },
        { name: 'Full voice interaction', included: true },
        { name: 'AI-powered insights', included: true },
        { name: 'Gift planning with suggestions', included: true },
        { name: 'Social radar monitoring', included: true },
        { name: 'Relationship analytics', included: true },
        { name: 'Team collaboration', included: false },
        { name: 'Priority support', included: false }
      ],
      cta: 'Start 14-Day Free Trial',
      popular: true
    },
    {
      name: 'Professional',
      description: 'For professionals and power users',
      icon: <Crown className="w-6 h-6" />,
      price: {
        monthly: 25,
        annual: 250 // $20.83/month when paid annually
      },
      badge: 'Best Value',
      key: 'professional',
      features: [
        { name: 'Unlimited contacts', included: true },
        { name: 'Premium relationship mapping', included: true },
        { name: 'Advanced AI check-in planning', included: true },
        { name: 'All contact import methods', included: true },
        { name: 'Mobile app access', included: true },
        { name: 'Full voice interaction', included: true },
        { name: 'Advanced AI insights', included: true },
        { name: 'Professional gift planning', included: true },
        { name: 'Complete social radar', included: true },
        { name: 'Advanced analytics & reporting', included: true },
        { name: 'Team collaboration (5 members)', included: true },
        { name: 'Priority email support', included: true }
      ],
      cta: 'Start 14-Day Free Trial',
      popular: false
    },
    {
      name: 'Enterprise',
      description: 'For organizations and large teams',
      icon: <Zap className="w-6 h-6" />,
      price: {
        monthly: null,
        annual: null
      },
      badge: null,
      key: 'enterprise',
      features: [
        { name: 'Unlimited contacts & team members', included: true },
        { name: 'Custom relationship models', included: true },
        { name: 'Enterprise-grade AI insights', included: true },
        { name: 'All contact import methods', included: true },
        { name: 'Mobile app access', included: true },
        { name: 'Full voice interaction', included: true },
        { name: 'White-label options', included: true },
        { name: 'Advanced gift planning & budgets', included: true },
        { name: 'Organization social insights', included: true },
        { name: 'Custom analytics & reporting', included: true },
        { name: 'Unlimited team collaboration', included: true },
        { name: '24/7 dedicated support', included: true }
      ],
      cta: 'Contact Sales',
      popular: false
    }
  ];

  // Convert API data to component format
  const getFormattedPlans = () => {
    if (!pricingData || !pricingData.plans || pricingData.plans.length === 0) {
      return fallbackPricingPlans;
    }

    return pricingData.plans.map(apiPlan => {
      const allocations = apiPlan.included_allocations || {};
      
      return {
        name: apiPlan.name,
        description: apiPlan.description,
        icon: getPlanIcon(apiPlan.key),
        price: apiPlan.pricing,
        badge: apiPlan.badge,
        key: apiPlan.key,
        features: [
          { 
            name: `Up to ${allocations.contacts_limit === -1 ? 'unlimited' : allocations.contacts_limit} contacts`, 
            included: true 
          },
          { 
            name: `${allocations.voice_minutes || 0} voice minutes${allocations.voice_minutes === -1 ? ' (unlimited)' : ''}`, 
            included: allocations.voice_minutes > 0 || allocations.voice_minutes === -1 
          },
          { 
            name: `${allocations.sms_count || 0} SMS messages${allocations.sms_count === -1 ? ' (unlimited)' : ''}`, 
            included: allocations.sms_count > 0 || allocations.sms_count === -1 
          },
          { 
            name: `${allocations.storage_gb || 0}GB storage${allocations.storage_gb === -1 ? ' (unlimited)' : ''}`, 
            included: allocations.storage_gb > 0 || allocations.storage_gb === -1 
          },
          { name: 'AI-powered insights', included: apiPlan.features.includes('ai_insights') },
          { name: 'Gift planning', included: apiPlan.features.includes('gift_planning') },
          { name: 'Social radar', included: apiPlan.features.includes('social_radar') },
          { name: 'Advanced analytics', included: apiPlan.features.includes('advanced_analytics') },
          { name: 'Priority support', included: apiPlan.features.includes('priority_support') },
          { name: 'White-label options', included: apiPlan.features.includes('white_label') }
        ],
        cta: apiPlan.cta,
        popular: apiPlan.is_most_popular
      };
    });
  };

  const pricingPlans = getFormattedPlans();

  // Generate comparison features from dynamic plan data
  const getComparisonFeatures = () => {
    if (pricingLoading || !pricingPlans.length) {
      return []; // Return empty during loading
    }

    return [
      {
        category: 'Core Features',
        features: [
          {
            name: 'Contact Management',
            starter: pricingPlans.find(p => p.key === 'starter')?.features.find(f => f.name.includes('contacts'))?.name?.replace('Up to ', '') || '25 contacts',
            personal: pricingPlans.find(p => p.key === 'personal')?.features.find(f => f.name.includes('contacts'))?.name?.replace('Up to ', '') || '200 contacts',
            professional: pricingPlans.find(p => p.key === 'professional')?.features.find(f => f.name.includes('contacts'))?.name?.replace('Up to ', '') || 'Unlimited',
            enterprise: pricingPlans.find(p => p.key === 'enterprise')?.features.find(f => f.name.includes('contacts'))?.name?.replace('Up to ', '') || 'Unlimited'
          },
          {
            name: 'Voice Minutes',
            starter: pricingPlans.find(p => p.key === 'starter')?.features.find(f => f.name.includes('voice minutes'))?.name || '0 voice minutes',
            personal: pricingPlans.find(p => p.key === 'personal')?.features.find(f => f.name.includes('voice minutes'))?.name || '1000 voice minutes',
            professional: pricingPlans.find(p => p.key === 'professional')?.features.find(f => f.name.includes('voice minutes'))?.name || '5000 voice minutes',
            enterprise: pricingPlans.find(p => p.key === 'enterprise')?.features.find(f => f.name.includes('voice minutes'))?.name || 'Unlimited voice minutes'
          }
        ]
      },
      {
        category: 'AI Features',
        features: [
          {
            name: 'AI Insights',
            starter: pricingPlans.find(p => p.key === 'starter')?.features.find(f => f.name.includes('AI-powered insights'))?.included || false,
            personal: pricingPlans.find(p => p.key === 'personal')?.features.find(f => f.name.includes('AI-powered insights'))?.included || false,
            professional: pricingPlans.find(p => p.key === 'professional')?.features.find(f => f.name.includes('AI-powered insights'))?.included || false,
            enterprise: pricingPlans.find(p => p.key === 'enterprise')?.features.find(f => f.name.includes('AI-powered insights'))?.included || false
          },
          {
            name: 'Gift Planning',
            starter: pricingPlans.find(p => p.key === 'starter')?.features.find(f => f.name.includes('Gift planning'))?.included || false,
            personal: pricingPlans.find(p => p.key === 'personal')?.features.find(f => f.name.includes('Gift planning'))?.included || false,
            professional: pricingPlans.find(p => p.key === 'professional')?.features.find(f => f.name.includes('Gift planning'))?.included || false,
            enterprise: pricingPlans.find(p => p.key === 'enterprise')?.features.find(f => f.name.includes('Gift planning'))?.included || false
          }
        ]
      },
      {
        category: 'Advanced Features',
        features: [
          {
            name: 'Social Radar',
            starter: pricingPlans.find(p => p.key === 'starter')?.features.find(f => f.name.includes('Social radar'))?.included || false,
            personal: pricingPlans.find(p => p.key === 'personal')?.features.find(f => f.name.includes('Social radar'))?.included || false,
            professional: pricingPlans.find(p => p.key === 'professional')?.features.find(f => f.name.includes('Social radar'))?.included || false,
            enterprise: pricingPlans.find(p => p.key === 'enterprise')?.features.find(f => f.name.includes('Social radar'))?.included || false
          },
          {
            name: 'Analytics',
            starter: pricingPlans.find(p => p.key === 'starter')?.features.find(f => f.name.includes('Advanced analytics'))?.included || false,
            personal: pricingPlans.find(p => p.key === 'personal')?.features.find(f => f.name.includes('Advanced analytics'))?.included || false,
            professional: pricingPlans.find(p => p.key === 'professional')?.features.find(f => f.name.includes('Advanced analytics'))?.included || false,
            enterprise: pricingPlans.find(p => p.key === 'enterprise')?.features.find(f => f.name.includes('Advanced analytics'))?.included || false
          }
        ]
      },
      {
        category: 'Support',
        features: [
          {
            name: 'Support Level',
            starter: 'Community',
            personal: 'Email',
            professional: pricingPlans.find(p => p.key === 'professional')?.features.find(f => f.name.includes('Priority support'))?.included ? 'Priority' : 'Email',
            enterprise: '24/7 dedicated'
          }
        ]
      }
    ];
  };

  const comparisonFeatures = getComparisonFeatures();

  const faqs = [
    {
      question: 'Can I change plans at any time?',
      answer: 'Yes! You can upgrade or downgrade your plan at any time. Changes take effect immediately, and we\'ll prorate any billing differences.'
    },
    {
      question: 'How does usage metering work?',
      answer: 'Our platform automatically tracks consumption in real-time with detailed analytics. You can view usage history, set limits, and export reports at any time.'
    },
    {
      question: 'Is Stripe integration included?',
      answer: 'Yes! Full Stripe integration is built-in with checkout, webhooks, customer portal, refunds, and subscription management - no additional setup required.'
    },
    {
      question: 'Do you support prepaid wallets?',
      answer: 'Absolutely. Our prepaid wallet system includes top-up functionality, balance tracking, transaction history, and automatic deductions based on usage.'
    },
    {
      question: 'What about security and compliance?',
      answer: 'We follow enterprise-grade security with SOC 2 compliance, AES-256 encryption, JWT authentication, and RBAC. Your data is always protected.'
    },
    {
      question: 'Can I customize pricing dynamically?',
      answer: 'Yes! The Monetization Studio allows admins to manage pricing, create plans, set features, and control approvals with maker-checker workflows.'
    }
  ];

  return (
    <MarketingLayout>
      <HeroSection />
      
      {/* Features Summary Section */}
      <section className="py-20 bg-white dark:bg-gray-950">
        <div className="container mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4 dark:text-gray-100">
              Everything you need to monetize your SaaS
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto dark:text-gray-300">
              Our enterprise-grade platform provides complete subscription management, usage tracking, 
              billing automation, and pricing governance - all integrated and ready to use.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: <CreditCard className="w-8 h-8 text-indigo-600" />,
                title: "Subscription Management",
                description: "Flexible plans with free trials, upgrades, downgrades, and automated billing cycles"
              },
              {
                icon: <BarChart3 className="w-8 h-8 text-cyan-600" />,
                title: "Usage Tracking", 
                description: "Real-time consumption metering with detailed analytics and historical reporting"
              },
              {
                icon: <Wallet className="w-8 h-8 text-purple-600" />,
                title: "Prepaid Wallets",
                description: "Built-in top-up system with balance management and transaction history"
              },
              {
                icon: <DollarSign className="w-8 h-8 text-pink-600" />,
                title: "Stripe Integration",
                description: "Secure payment processing with webhooks, refunds, and customer portal built-in"
              },
              {
                icon: <Settings className="w-8 h-8 text-green-600" />,
                title: "Monetization Studio",
                description: "Complete admin dashboard for pricing governance, plan management, and approvals"
              },
              {
                icon: <Lock className="w-8 h-8 text-orange-600" />,
                title: "RBAC & Security",
                description: "Enterprise-grade role-based access control with JWT authentication and encryption"
              }
            ].map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center p-6 rounded-xl bg-gray-50 hover:shadow-lg transition-all duration-300 border border-gray-100 dark:bg-gray-900 dark:border-gray-800"
              >
                <div className="w-16 h-16 bg-white rounded-lg flex items-center justify-center mx-auto mb-4 shadow-sm dark:bg-gray-800">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3 dark:text-gray-100">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed dark:text-gray-300">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 bg-gradient-to-br from-indigo-50 via-white to-cyan-50 dark:from-slate-950 dark:via-slate-900 dark:to-slate-800">
        <div className="container mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto mb-16"
          >
            <h2 className="text-3xl lg:text-5xl font-bold text-gray-900 mb-6 dark:text-gray-100">
              Simple, Transparent
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-cyan-600">
                Pricing
              </span>
            </h2>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed dark:text-gray-300">
              Choose the perfect plan for your business needs. 
              Start free and scale as you grow.
            </p>
            
            {/* Billing Toggle */}
            <div className="flex items-center justify-center space-x-4 mb-8">
              <span className={`${billingCycle === 'monthly' ? 'text-gray-900' : 'text-gray-500'} dark:text-gray-300`}>
                Monthly
              </span>
              <button
                onClick={() => setBillingCycle(billingCycle === 'monthly' ? 'annual' : 'monthly')}
                className="relative w-14 h-7 bg-gray-300 rounded-full transition-colors focus:outline-none dark:bg-gray-700"
                aria-label="Toggle billing cycle"
              >
                <div
                  className={`absolute top-1 w-5 h-5 bg-white rounded-full transition-transform ${
                    billingCycle === 'annual' ? 'translate-x-8 bg-indigo-600' : 'translate-x-1'
                  }`}
                />
              </button>
              <span className={`${billingCycle === 'annual' ? 'text-gray-900' : 'text-gray-500'} dark:text-gray-300`}>
                Annual
              </span>
              {billingCycle === 'annual' && (
                <span className="bg-green-100 text-green-800 text-sm px-2 py-1 rounded-full dark:bg-green-900/30 dark:text-green-200">
                  Save up to 20%
                </span>
              )}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-20 bg-white dark:bg-gray-950">
        <div className="container mx-auto px-6 lg:px-8">
          {pricingLoading ? (
            // Skeleton Loading State
            <div className="grid lg:grid-cols-4 gap-8">
              {[1, 2, 3, 4].map((_, index) => (
                <div key={index} className="animate-pulse">
                  <div className="rounded-2xl shadow-lg border-2 border-gray-200 bg-white dark:bg-gray-900 dark:border-gray-700 p-6">
                    <div className="text-center mb-6">
                      <div className="w-12 h-12 bg-gray-200 rounded-lg mx-auto mb-3 dark:bg-gray-700"></div>
                      <div className="h-6 bg-gray-200 rounded mb-2 dark:bg-gray-700"></div>
                      <div className="h-4 bg-gray-200 rounded w-3/4 mx-auto dark:bg-gray-700"></div>
                    </div>
                    <div className="text-center mb-6">
                      <div className="h-8 bg-gray-200 rounded mb-2 dark:bg-gray-700"></div>
                      <div className="h-4 bg-gray-200 rounded w-1/2 mx-auto dark:bg-gray-700"></div>
                    </div>
                    <div className="space-y-3 mb-6">
                      {[1, 2, 3, 4, 5].map((_, i) => (
                        <div key={i} className="flex items-center space-x-3">
                          <div className="w-5 h-5 bg-gray-200 rounded dark:bg-gray-700"></div>
                          <div className="h-4 bg-gray-200 rounded flex-1 dark:bg-gray-700"></div>
                        </div>
                      ))}
                    </div>
                    <div className="h-10 bg-gray-200 rounded dark:bg-gray-700"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : pricingError ? (
            // Error State
            <div className="text-center py-12">
              <div className="text-red-600 mb-4">
                <Shield className="w-12 h-12 mx-auto mb-2" />
                <h3 className="text-lg font-semibold">Failed to Load Pricing</h3>
                <p className="text-sm">{pricingError}</p>
              </div>
              <Button onClick={fetchPricingData} variant="outline">
                <ArrowRight className="w-4 h-4 mr-2" />
                Try Again
              </Button>
            </div>
          ) : (
            // Pricing Cards
            <>
              {/* Coupon Validation Section */}
              {pricingData?.source === 'database' && (
                <div className="max-w-md mx-auto mb-8">
                  <div className="bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-lg p-4 dark:from-green-900/10 dark:to-blue-900/10 dark:border-green-700">
                    <h3 className="font-semibold text-gray-900 mb-2 dark:text-gray-100">Have a coupon code?</h3>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        placeholder="Enter coupon code"
                        value={couponCode}
                        onChange={(e) => setCouponCode(e.target.value)}
                        className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-100"
                        disabled={couponValidating}
                      />
                      <Button
                        onClick={() => validateCoupon(couponCode, 'personal')}
                        disabled={couponValidating || !couponCode.trim()}
                        size="sm"
                      >
                        {couponValidating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Apply'}
                      </Button>
                    </div>
                    {couponError && (
                      <p className="text-red-600 text-sm mt-2">{couponError}</p>
                    )}
                    {appliedCoupon && (
                      <div className="mt-2 p-2 bg-green-100 border border-green-300 rounded text-sm dark:bg-green-900/20 dark:border-green-700">
                        <p className="text-green-800 font-medium dark:text-green-200">
                          🎉 {appliedCoupon.name} applied!
                        </p>
                        <p className="text-green-700 text-xs dark:text-green-300">
                          {appliedCoupon.description}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

                <div className="grid lg:grid-cols-4 gap-8">
                {pricingPlans.map((plan, index) => {
                  const originalMonthlyPrice = billingCycle === 'monthly' 
                    ? plan.price.monthly 
                    : (plan.price.annual ? Math.round(plan.price.annual / 12) : null);
                  
                  const discountedPrice = appliedCoupon && originalMonthlyPrice
                    ? calculateDiscountedPrice(originalMonthlyPrice, appliedCoupon)
                    : originalMonthlyPrice;

                  const planLabel = getPlanLabel(plan.key);
                  const isCurrentPlan = getPlanRelationship(plan.key) === 'current';
                  const planRelationship = getPlanRelationship(plan.key);

                  return (
                    <motion.div
                      key={plan.name}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.6, delay: index * 0.1 }}
                      className={`relative rounded-2xl shadow-lg border-2 transition-all duration-300 hover:shadow-xl bg-white ${
                        isCurrentPlan ? 'border-green-500 ring-2 ring-green-200' : 
                        plan.popular ? 'border-indigo-500 scale-105' : 'border-gray-200 hover:border-indigo-300'
                      } dark:bg-gray-900 dark:border-gray-700 ${
                        isCurrentPlan ? 'dark:border-green-400 dark:ring-green-800' : ''
                      }`}
                    >
                      {/* Show current plan badge or original badge */}
                      {isCurrentPlan ? (
                        <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                          <span className="bg-green-600 text-white px-4 py-2 rounded-full text-sm font-medium">
                            Your Current Plan
                          </span>
                        </div>
                      ) : plan.badge ? (
                        <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                          <span className="bg-indigo-600 text-white px-4 py-2 rounded-full text-sm font-medium">
                            {plan.badge}
                          </span>
                        </div>
                      ) : planLabel && planRelationship !== 'available' ? (
                        <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                          <span className={`px-4 py-2 rounded-full text-sm font-medium ${
                            planRelationship === 'upgrade' 
                              ? 'bg-blue-600 text-white' 
                              : 'bg-orange-600 text-white'
                          }`}>
                            {planLabel}
                          </span>
                        </div>
                      ) : null}
                      
                      <div className="p-6">
                        {/* Plan Header */}
                        <div className="text-center mb-6">
                          <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center text-indigo-600 mx-auto mb-3 dark:bg-indigo-900/30 dark:text-indigo-300">
                            {plan.icon}
                          </div>
                          <h3 className="text-xl font-bold text-gray-900 mb-2 dark:text-gray-100">{plan.name}</h3>
                          <p className="text-gray-600 text-sm dark:text-gray-300">{plan.description}</p>
                        </div>

                        {/* Pricing */}
                        <div className="text-center mb-6">
                          {originalMonthlyPrice === null ? (
                            <div className="text-3xl font-bold text-gray-900 dark:text-gray-100">Custom</div>
                          ) : originalMonthlyPrice === 0 ? (
                            <div className="text-3xl font-bold text-gray-900 dark:text-gray-100">Free</div>
                          ) : (
                            <>
                              {appliedCoupon && discountedPrice !== originalMonthlyPrice && (
                                <div className="text-lg text-gray-500 line-through dark:text-gray-400">
                                  ${originalMonthlyPrice}
                                </div>
                              )}
                              <div className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                                ${discountedPrice !== null ? Math.round(discountedPrice) : originalMonthlyPrice}
                              </div>
                              <div className="text-gray-600 dark:text-gray-300">
                                /{billingCycle === 'monthly' ? 'month' : 'month, paid annually'}
                              </div>
                              {billingCycle === 'annual' && plan.price.annual && plan.price.monthly > 0 && (
                                <div className="text-sm text-green-600 mt-1 dark:text-green-300">
                                  Save ${(plan.price.monthly * 12) - plan.price.annual}/year
                                </div>
                              )}
                              {appliedCoupon && discountedPrice !== originalMonthlyPrice && (
                                <div className="text-sm text-green-600 font-medium dark:text-green-300">
                                  {appliedCoupon.discount_type === 'percentage' 
                                    ? `${appliedCoupon.discount_value}% off applied!`
                                    : `$${appliedCoupon.discount_value} off applied!`
                                  }
                                </div>
                              )}
                            </>
                          )}
                        </div>

                        {/* Features */}
                        <div className="space-y-3 mb-6">
                          {plan.features.map((feature, idx) => (
                            <div key={idx} className="flex items-center space-x-3">
                              {feature.included ? (
                                <Check className="w-5 h-5 text-green-500 flex-shrink-0" />
                              ) : (
                                <X className="w-5 h-5 text-gray-300 flex-shrink-0 dark:text-gray-600" />
                              )}
                              <span className={`text-sm ${feature.included ? 'text-gray-700 dark:text-gray-300' : 'text-gray-400 dark:text-gray-500'}`}>
                                {feature.name}
                              </span>
                            </div>
                          ))}
                        </div>

                        {/* CTA Button */}
                        {isCurrentPlan ? (
                          <Button
                            disabled
                            className="w-full bg-green-100 text-green-800 cursor-not-allowed dark:bg-green-900/30 dark:text-green-200"
                          >
                            <Check className="w-4 h-4 mr-2" />
                            Selected
                          </Button>
                        ) : plan.name === 'Enterprise' ? (
                          <Link to="/contact" className="block w-full">
                            <Button className="w-full bg-gray-100 hover:bg-gray-200 text-gray-900 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700">
                              Contact Sales
                            </Button>
                          </Link>
                        ) : plan.name === 'Starter' ? (
                          currentUser ? (
                            <Link to="/app" className="block w-full">
                              <Button className="w-full bg-gray-100 hover:bg-gray-200 text-gray-900 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700">
                                Access Free Plan
                                <ArrowRight className="w-4 h-4 ml-2" />
                              </Button>
                            </Link>
                          ) : (
                            <Link to="/auth/signup" className="block w-full">
                              <Button className="w-full bg-gray-100 hover:bg-gray-200 text-gray-900 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700">
                                Start Free Forever
                                <ArrowRight className="w-4 h-4 ml-2" />
                              </Button>
                            </Link>
                          )
                        ) : (
                          currentUser ? (
                            <Link
                              to={`/checkout?plan=${plan.key}&billing=${billingCycle}${appliedCoupon ? `&coupon=${appliedCoupon.code}` : ''}`}
                              className="block w-full"
                            >
                              <Button
                                className={`w-full ${
                                  plan.popular
                                    ? 'bg-indigo-600 hover:bg-indigo-700 text-white'
                                    : 'bg-gray-100 hover:bg-gray-200 text-gray-900 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700'
                                }`}
                              >
                                {planRelationship === 'upgrade' ? 'Upgrade Plan' : 
                                 planRelationship === 'downgrade' ? 'Downgrade Plan' : 
                                 'Select Plan'}
                                <ArrowRight className="w-4 h-4 ml-2" />
                              </Button>
                            </Link>
                          ) : (
                            <Link to={`/auth/login?redirect=${encodeURIComponent('/checkout?plan=' + plan.key + '&billing=' + billingCycle + (appliedCoupon ? '&coupon=' + appliedCoupon.code : ''))}`} className="block w-full">
                              <Button
                                className={`w-full ${
                                  plan.popular
                                    ? 'bg-indigo-600 hover:bg-indigo-700 text-white'
                                    : 'bg-gray-100 hover:bg-gray-200 text-gray-900 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700'
                                }`}
                              >
                                Start 14-Day Free Trial
                                <ArrowRight className="w-4 h-4 ml-2" />
                              </Button>
                            </Link>
                          )
                        )}
                      </div>
                    </motion.div>
                  );
                })}
              </div>

              {/* API Source Indicator */}
              {pricingData?.source && (
                <div className="text-center mt-8">
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Pricing data: {pricingData.source === 'database' ? '🔄 Live API' : '📋 Fallback'}
                    {pricingData.updated_at && ` • Updated: ${new Date(pricingData.updated_at).toLocaleString()}`}
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* Feature Comparison Table */}
      <section className="py-20 bg-gray-50 dark:bg-gray-950">
        <div className="container mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4 dark:text-gray-100">
              Detailed Feature Comparison
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto dark:text-gray-300">
              Compare all features across our plans to find the perfect fit
            </p>
          </motion.div>

          <div className="rounded-2xl shadow-lg overflow-hidden bg-white dark:bg-gray-900">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200 dark:bg-gray-900 dark:border-gray-800">
                    <th className="text-left py-4 px-6 font-semibold text-gray-900 dark:text-gray-100">Features</th>
                    <th className="text-center py-4 px-6 font-semibold text-gray-900 dark:text-gray-100">Starter</th>
                    <th className="text-center py-4 px-6 font-semibold text-gray-900 dark:text-gray-100">Personal</th>
                    <th className="text-center py-4 px-6 font-semibold text-gray-900 dark:text-gray-100">Professional</th>
                    <th className="text-center py-4 px-6 font-semibold text-gray-900 dark:text-gray-100">Enterprise</th>
                  </tr>
                </thead>
                <tbody>
                  {comparisonFeatures.map((category, categoryIndex) => (
                    <React.Fragment key={category.category}>
                      <tr className="bg-indigo-50 dark:bg-indigo-950/40">
                        <td colSpan="5" className="py-3 px-6 font-semibold text-indigo-900 border-b border-gray-200 dark:text-indigo-200 dark:border-gray-800">
                          {category.category}
                        </td>
                      </tr>
                      {category.features.map((feature, featureIndex) => (
                        <tr key={featureIndex} className="border-b border-gray-100 hover:bg-gray-50 dark:border-gray-800 dark:hover:bg-gray-900">
                          <td className="py-3 px-6 text-gray-900 dark:text-gray-100">{feature.name}</td>
                          <td className="py-3 px-6 text-center">
                            {typeof feature.starter === 'boolean' ? (
                              feature.starter ? (
                                <Check className="w-5 h-5 text-green-500 mx-auto" />
                              ) : (
                                <X className="w-5 h-5 text-gray-300 mx-auto dark:text-gray-600" />
                              )
                            ) : (
                              <span className="text-gray-700 dark:text-gray-300">{feature.starter}</span>
                            )}
                          </td>
                          <td className="py-3 px-6 text-center">
                            {typeof feature.personal === 'boolean' ? (
                              feature.personal ? (
                                <Check className="w-5 h-5 text-green-500 mx-auto" />
                              ) : (
                                <X className="w-5 h-5 text-gray-300 mx-auto dark:text-gray-600" />
                              )
                            ) : (
                              <span className="text-gray-700 dark:text-gray-300">{feature.personal}</span>
                            )}
                          </td>
                          <td className="py-3 px-6 text-center">
                            {typeof feature.professional === 'boolean' ? (
                              feature.professional ? (
                                <Check className="w-5 h-5 text-green-500 mx-auto" />
                              ) : (
                                <X className="w-5 h-5 text-gray-300 mx-auto dark:text-gray-600" />
                              )
                            ) : (
                              <span className="text-gray-700 dark:text-gray-300">{feature.professional}</span>
                            )}
                          </td>
                          <td className="py-3 px-6 text-center">
                            {typeof feature.enterprise === 'boolean' ? (
                              feature.enterprise ? (
                                <Check className="w-5 h-5 text-green-500 mx-auto" />
                              ) : (
                                <X className="w-5 h-5 text-gray-300 mx-auto dark:text-gray-600" />
                              )
                            ) : (
                              <span className="text-gray-700 dark:text-gray-300">{feature.enterprise}</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white dark:bg-gray-950">
        <div className="container mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4 dark:text-gray-100">
              Frequently Asked Questions
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto dark:text-gray-300">
              Everything you need to know about SaaS-Base pricing
            </p>
          </motion.div>

          <div className="max-w-3xl mx-auto space-y-6">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="rounded-lg p-6 bg-gray-50 dark:bg-gray-900"
              >
                <h3 className="font-semibold text-gray-900 mb-3 dark:text-gray-100">{faq.question}</h3>
                <p className="text-gray-600 leading-relaxed dark:text-gray-300">{faq.answer}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="py-20 bg-gray-50 dark:bg-gray-950">
        <div className="container mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-12 dark:text-gray-100">
              Enterprise-Ready Platform
            </h2>
            <div className="grid md:grid-cols-4 gap-8">
              <div className="text-center">
                <Shield className="w-12 h-12 text-indigo-600 mx-auto mb-4" />
                <h3 className="font-semibold text-gray-900 mb-2 dark:text-gray-100">SOC 2 Compliant</h3>
                <p className="text-gray-600 text-sm dark:text-gray-300">Enterprise-grade security standards</p>
              </div>
              <div className="text-center">
                <Globe className="w-12 h-12 text-indigo-600 mx-auto mb-4" />
                <h3 className="font-semibold text-gray-900 mb-2 dark:text-gray-100">Global Scale</h3>
                <p className="text-gray-600 text-sm dark:text-gray-300">Built for worldwide SaaS businesses</p>
              </div>
              <div className="text-center">
                <TrendingUp className="w-12 h-12 text-indigo-600 mx-auto mb-4" />
                <h3 className="font-semibold text-gray-900 mb-2 dark:text-gray-100">Built for Growth</h3>
                <p className="text-gray-600 text-sm dark:text-gray-300">Scale from startup to enterprise</p>
              </div>
              <div className="text-center">
                <BarChart3 className="w-12 h-12 text-indigo-600 mx-auto mb-4" />
                <h3 className="font-semibold text-gray-900 mb-2 dark:text-gray-100">99.9% Uptime</h3>
                <p className="text-gray-600 text-sm dark:text-gray-300">Reliable service guarantee</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-br from-indigo-600 to-purple-700 dark:from-indigo-700 dark:to-purple-800">
        <div className="container mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center text-white"
          >
            <h2 className="text-3xl lg:text-4xl font-bold mb-4">
              Ready to Transform Your SaaS Monetization?
            </h2>
            <p className="text-xl text-indigo-100 mb-8 max-w-2xl mx-auto">
              Join leading SaaS companies using SaaS-Base to scale their revenue operations.
            </p>
            <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-4">
              <Link to="/auth/signup">
                <Button size="lg" className="bg-white text-indigo-600 hover:bg-gray-50 px-8 py-4">
                  Start Free Trial
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 px-8 py-4">
                  Contact Sales
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </MarketingLayout>
  );
};

export default HomePage;